/** Drifting cartoon clouds: puffy cumulus or long lens-shaped streaks. */
import type { CloudsLayer } from '@dropstack/core';
import { lerp, wrap, type SceneSize } from '../layout.ts';
import { range, rngFor } from '../noise.ts';
import { circleBlob, ellipsePath, rgba, shade, type Circle, type Ctx } from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

interface CloudPlan {
  /** Start x as a fraction of the drift track. */
  track: number;
  y: number;
  w: number;
  puffs: Circle[];
}

/** Deterministic cloud shapes, relative to the cloud's centre-bottom. */
function planClouds(layer: CloudsLayer, env: PaintEnv): CloudPlan[] {
  const rng = rngFor(env.seed, 1);
  const { size } = env;
  const n = Math.max(0, Math.floor(layer.count));
  return Array.from({ length: n }, (_, i) => {
    const w = layer.size * size.unit * range(rng, 0.75, 1.3);
    const y = lerp(layer.yMin, layer.yMax, n === 1 ? 0.5 : (i + range(rng, 0, 1)) / n) * size.height;
    const puffs: Circle[] = [];
    if (layer.style === 'long') {
      // Three stretched lenses stacked slightly; drawn as ellipses below.
      for (let k = 0; k < 3; k++) puffs.push({ x: (k - 1) * w * 0.22, y: -w * 0.03 * (k === 1 ? 1.4 : 1), r: w * (k === 1 ? 0.42 : 0.32) });
    } else {
      const bumps = 3 + Math.floor(range(rng, 0, 2.99));
      puffs.push({ x: -w * 0.3, y: -w * 0.1, r: w * 0.16 }, { x: w * 0.3, y: -w * 0.1, r: w * 0.17 });
      for (let k = 0; k < bumps; k++) {
        const t = (k + 0.5) / bumps;
        puffs.push({ x: (t - 0.5) * w * 0.62, y: -w * (0.2 + 0.1 * Math.sin(t * Math.PI)) - range(rng, 0, w * 0.05), r: w * range(rng, 0.18, 0.26) });
      }
    }
    // A lone cloud (e.g. Table Mountain's tablecloth) starts centred.
    const track = n === 1 ? 0.5 : (i + range(rng, 0.1, 0.9)) / n;
    return { track, y, w, puffs };
  });
}

function drawCloud(ctx: Ctx, layer: CloudsLayer, plan: CloudPlan, cx: number, size: SceneSize): void {
  const rim = shade(layer.shade, -0.12);
  if (layer.style === 'long') {
    ctx.beginPath();
    for (const p of plan.puffs) ellipsePath(ctx, cx + p.x, plan.y + p.y, p.r, p.r * 0.2);
    ctx.lineJoin = 'round';
    ctx.strokeStyle = rgba(rim, 0.55);
    ctx.lineWidth = Math.max(1, size.unit * 0.004);
    ctx.stroke();
    ctx.fillStyle = layer.color;
    ctx.fill();
    ctx.beginPath();
    ellipsePath(ctx, cx, plan.y + plan.w * 0.02, plan.w * 0.5, plan.w * 0.035);
    ctx.fillStyle = rgba(layer.shade, 0.8);
    ctx.fill();
    return;
  }
  const circles = plan.puffs.map((p) => ({ x: cx + p.x, y: plan.y + p.y, r: p.r }));
  // Flat-ish bottom: a wide rounded base under the puffs.
  circles.push({ x: cx - plan.w * 0.12, y: plan.y - plan.w * 0.05, r: plan.w * 0.14 }, { x: cx + plan.w * 0.12, y: plan.y - plan.w * 0.05, r: plan.w * 0.15 });
  const top = plan.y - plan.w * 0.5;
  const g = ctx.createLinearGradient(0, top, 0, plan.y + plan.w * 0.1);
  g.addColorStop(0, layer.color);
  g.addColorStop(0.62, layer.color);
  g.addColorStop(1, layer.shade);
  circleBlob(ctx, circles, g, rgba(rim, 0.7), size, 0.8);
  // Glossy top-left puff.
  const lead = plan.puffs[2] ?? plan.puffs[0];
  if (lead) {
    ctx.beginPath();
    ellipsePath(ctx, cx + lead.x - lead.r * 0.25, plan.y + lead.y - lead.r * 0.35, lead.r * 0.45, lead.r * 0.25, -0.3);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fill();
  }
}

export const cloudsPainter: Painter<CloudsLayer> = {
  moving(ctx, layer, env) {
    const { size, time } = env;
    const margin = layer.size * size.unit * 0.8;
    const min = -size.bleed - margin;
    const max = size.width + size.bleed + margin;
    const drift = (layer.speed * size.unit * time) / 60;
    for (const plan of planClouds(layer, env)) {
      const cx = wrap(min + plan.track * (max - min) + drift, min, max);
      drawCloud(ctx, layer, plan, cx, size);
    }
  },
  animated: (layer) => layer.speed > 0,
};
